import type {MouseEvent, PointerEvent} from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Text, ThemeIcon, UnstyledButton} from "@mantine/core";
import {GPTMetaContext} from "../MetaProvider";
import {useHasRole} from "../../../../../featureFlags/featureFlags";
import {HasEditorPrivileges, isExternalSourceType} from "../../../../../types";
import {useCitationPreview} from "../../../citation-preview/useCitationPreview";
import {
    targetFromSource,
    usePrefetchCitationParts,
} from "../../../citation-preview/useCitationParts";
import classes from "./Citation.module.css";

type LinkProps = {
    reference: string;
};

/** How long a hover must linger on the marker before the preview opens. */
const HOVER_OPEN_DELAY_MS = 250;
/** A marker must stay in view this long before it prefetches, so a fast
 *  scroll past it does not trigger a fetch. */
export const PREFETCH_DWELL_MS = 400;

/** Parses a "[N]" marker into its 0-based citation index, or null if invalid. */
function parseCitationIndex(reference: string): number | null {
    const raw = reference.replace(/[[\]]/g, "");
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : null;
}

/** True on a device with a real pointer that can hover. False on touch-only. */
function supportsHover(): boolean {
    return (
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(hover: hover)").matches
    );
}

export const Citation = ({reference}: LinkProps) => {
    const meta = useContext(GPTMetaContext);
    // Only editors and higher get the preview.
    const {hasRole: previewEnabled} = useHasRole(HasEditorPrivileges);
    const {state, openPreview, scheduleClose, cancelScheduledClose} =
        useCitationPreview();
    const prefetch = usePrefetchCitationParts();
    const anchorRef = useRef<HTMLButtonElement>(null);
    const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Tracks the last target this marker prefetched, so a visibility
    // re-trigger never issues the same fetch twice.
    const prefetchedTargetKeyRef = useRef<string | null>(null);
    const [hovered, setHovered] = useState(false);

    const number = useMemo(() => parseCitationIndex(reference), [reference]);

    // AssistantMessage renumbers the sources before it passes them here, to
    // match the marker numbers in the text.
    const source = useMemo(() => {
        if (number === null) return null;
        return (
            meta?.sources?.find((candidate) => candidate.index === number) ??
            null
        );
    }, [meta?.sources, number]);

    const clearHoverTimer = () => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    };

    // Clear a pending hover timer on unmount so it never fires after removal.
    useEffect(() => clearHoverTimer, []);

    // An ExternalSource has no DIB content behind it. Its marker renders
    // nothing; attribution for these sources shows elsewhere.
    const isExternal = isExternalSourceType(source?.type);
    const canOpen = previewEnabled && !!source && !meta?.isStreaming && !isExternal;

    // Warm the parts cache before the user hovers. A marker prefetches once
    // it scrolls into view, so a long thread does not fetch every citation
    // as soon as it loads.
    useEffect(() => {
        if (!canOpen || !source) return;
        const anchorEl = anchorRef.current;
        if (!anchorEl) return;

        const target = targetFromSource(source);
        if (!target?.resourceId) return;
        const targetKey = JSON.stringify(target);

        const prefetchOnce = () => {
            if (prefetchedTargetKeyRef.current === targetKey) return;
            prefetchedTargetKeyRef.current = targetKey;
            prefetch(target);
        };

        if (typeof IntersectionObserver === "undefined") {
            // jsdom has no IntersectionObserver. Prefetch right away instead.
            prefetchOnce();
            return;
        }

        let dwellTimer: ReturnType<typeof setTimeout> | null = null;
        const observer = new IntersectionObserver((entries) => {
            const visible = entries.some((entry) => entry.isIntersecting);
            if (visible && dwellTimer === null) {
                dwellTimer = setTimeout(() => {
                    dwellTimer = null;
                    prefetchOnce();
                    observer.disconnect();
                }, PREFETCH_DWELL_MS);
            } else if (!visible && dwellTimer !== null) {
                clearTimeout(dwellTimer);
                dwellTimer = null;
            }
        });
        observer.observe(anchorEl);

        return () => {
            if (dwellTimer !== null) clearTimeout(dwellTimer);
            observer.disconnect();
        };
    }, [canOpen, source, prefetch]);

    if (number === null || isExternal) {
        return null;
    }

    const numberPlusOne = number + 1;
    const isOpen = canOpen && state?.anchorEl === anchorRef.current;
    // The chip stays in its dark, "active" state for as long as it is
    // hovered or its preview is open. It never returns to grey while
    // the panel it anchors is still open.
    const isDark = hovered || isOpen;

    // The source name and section read as the accessible name, so a screen reader
    // announces what the number stands for.
    const accessibleName = source
        ? [source.name, source.segmentName].filter(Boolean).join(", ")
        : undefined;

    const badge = (
        <ThemeIcon
            className={`citation-badge ${classes.badge} ${isDark ? classes.badgeDark : ""}`}
            component="span"
            role="img"
            aria-label={accessibleName}
            title={accessibleName}
            size="xs"
            radius="xl"
            variant="light"
            color="gray"
            w="1rem"
            h="1rem"
            mx="1px"
        >
            <Text span fz="xs" c={isDark ? "white" : "var(--dib-ink)"} className={classes.badgeNumber}>
                {numberPlusOne}
            </Text>
        </ThemeIcon>
    );

    if (!canOpen || !source) {
        return badge;
    }

    const handleClick = (event: MouseEvent) => {
        // A citation marker can sit inside a DibAppLink anchor. Stop the
        // click here so it neither navigates nor reaches the anchor's own
        // onClick handler.
        event.preventDefault();
        event.stopPropagation();
        clearHoverTimer();
        openPreview(source, anchorRef.current, "click");
    };

    const handlePointerEnter = (event: PointerEvent) => {
        if (event.pointerType === "touch" || !supportsHover()) return;

        setHovered(true);
        // Hover also starts the fetch, so the panel is ready once it opens.
        prefetch(targetFromSource(source));

        if (isOpen && state?.openedBy === "hover") {
            // The pointer returns to the marker after moving toward the panel.
            cancelScheduledClose();
            return;
        }

        hoverTimerRef.current = setTimeout(() => {
            hoverTimerRef.current = null;
            openPreview(source, anchorRef.current, "hover");
        }, HOVER_OPEN_DELAY_MS);
    };

    const handlePointerLeave = () => {
        clearHoverTimer();
        setHovered(false);
        if (isOpen && state?.openedBy === "hover") {
            // Give the user time to move the pointer into the panel.
            scheduleClose();
        }
    };

    return (
        <UnstyledButton
            ref={anchorRef}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-label={accessibleName}
            className={classes.marker}
            onClick={handleClick}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
            style={{display: "inline-flex", verticalAlign: "middle"}}
        >
            {badge}
        </UnstyledButton>
    );
};
